import React, { FormEvent, useState } from 'react';
import * as Yup from 'yup';
import { GridColDef } from '@mui/x-data-grid';
import { addCommas } from '@persian-tools/persian-tools';
import { useWithdrawsQuery } from '../api/withdraw.api';
import { timeConverter, toGregorian } from 'utils/utils';
import { WithdrawStatusBullet } from 'components/bullet-status/withdraw-status-bullet';
import { SearchJsonObject } from 'components/search-field/search.form';
import { useGateway } from 'hooks';
import { BankLogoDetails } from 'components/bank-logo/bank-logo-details';
import { Withdraw, WithdrawBank } from '../../gateways/types/withdraw.types';
import { getSettlementStatusLabel } from '../utils';
import { ShebaSearchField } from 'components/formik/sheba-field/sheba-search-field';

const WithdrawShebaSearchField = (props: any) => (
    <ShebaSearchField {...props} hideMaskPlaceholder />
);

const PER_PAGE_COUNT = 15;

export type WithdrawSearch = {
    id?: string | number;
    iban?: string;
    ref?: string;
    status?: string;
    from_amount?: string | number;
    to_amount?: string | number;
    from_date?: string;
    to_date?: string;
} & Record<string, unknown>;

type UseWithdrawProps = {
    limitCount?: number;
    enabled?: boolean;
};

type WithdrawBankInfo = {
    fullname?: string;
    iban?: string;
    symbol?: string;
};

const parseBankInfo = (bank?: WithdrawBank): WithdrawBankInfo => {
    if (!bank?.bank_info) return {};

    try {
        return JSON.parse(bank.bank_info) as WithdrawBankInfo;
    } catch {
        return {};
    }
};

const firstBankInfo = (row: Withdraw): WithdrawBankInfo =>
    parseBankInfo(row.banks?.[0]);

const digitsOnlyInput = () => ({
    inputMode: 'numeric' as const,
    onInput: (event: FormEvent<HTMLInputElement>) => {
        event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '');
    },
});

export const getWithdrawApiSearch = (search: WithdrawSearch) => ({
    ...search,
    ...(search.from_date
        ? { from_date: toGregorian(String(search.from_date)) }
        : {}),
    ...(search.to_date
        ? { to_date: toGregorian(String(search.to_date)) }
        : {}),
});

const useWithdraw = ({ limitCount, enabled = true }: UseWithdrawProps = {}) => {
    const active = useGateway();

    const [search, setSearch] = useState<WithdrawSearch>({});
    const [page, setPage] = useState<number>(1);

    const apiSearch = getWithdrawApiSearch(search);

    const {
        data,
        isFetching: isLoadingData,
        isSuccess,
        refetch,
    } = useWithdrawsQuery(
        {
            workspaceId: active?.id as number,
            page,
            perPage: PER_PAGE_COUNT,
            search: apiSearch,
        },
        { skip: !enabled || active?.status !== 'activated' },
    );

    const rowsData: Withdraw[] = data?.items ?? [];
    const paginateInfo = data?.pagination;

    return {
        active,
        rowsData: limitCount ? rowsData.slice(0, limitCount) : rowsData,
        columns,
        searchJson,
        search,
        setSearch,
        page,
        perPage: PER_PAGE_COUNT,
        isLoadingData,
        isSuccess,
        refetch,
        paginateInfo:
            limitCount && limitCount < PER_PAGE_COUNT
                ? undefined
                : paginateInfo,
        setPage,
    };
};

export default useWithdraw;

const columns: GridColDef[] = [
    {
        field: 'id',
        headerName: '#',
        width: 80,
    },
    {
        field: 'amount',
        headerName: 'مبلغ (ریال)',
        minWidth: 150,
        renderCell: ({ row }) => addCommas(row.amount),
    },
    {
        field: 'banks',
        headerName: 'حساب بانکی',
        minWidth: 300,
        renderCell: ({ row }) => {
            const account = firstBankInfo(row);
            if (!account.iban) return '';

            return (
                <BankLogoDetails
                    iban={account.iban}
                    bankName={row.banks?.[0]?.name}
                    width="28px"
                    height="25px"
                    fontSize="13px"
                />
            );
        },
    },
    {
        field: 'account_owner',
        headerName: 'نام صاحب حساب',
        minWidth: 170,
        renderCell: ({ row }) => firstBankInfo(row).fullname ?? '',
    },
    {
        field: 'updated_at',
        headerName: 'زمان واریز',
        minWidth: 180,
        renderCell: ({ row }) =>
            row.status === 'accepted' || row.status === 'success'
                ? timeConverter(row.updated_at)
                : '',
    },
    {
        field: 'status',
        headerName: 'وضعیت',
        minWidth: 170,
        renderCell: ({ row }) => (
            <WithdrawStatusBullet
                status={row?.status}
                label={getSettlementStatusLabel(row?.status)}
            />
        ),
    },
];

const searchJson: SearchJsonObject = {
    fields: [
        {
            name: 'id',
            label: 'شناسه تسویه حساب',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fal fa-hashtag" />,
            inputProps: digitsOnlyInput(),
            gridSize: 6,
        },
        {
            name: 'iban',
            label: 'شماره شبا',
            component: WithdrawShebaSearchField,
            gridSize: 6,
        },
        {
            name: 'ref',
            label: 'کدپیگیری واریز',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-receipt" />,
            gridSize: 6,
        },
        {
            name: 'status',
            label: 'وضعیت',
            component: 'Select',
            icon: <i className="fa-light fa-circle-check" />,
            options: [
                { label: 'در انتظار پرداخت', value: 'created' },
                { label: 'ارسال برای شاپرک', value: 'send_shaparak' },
                { label: 'واریز شده', value: 'accepted' },
                { label: 'لغو شده', value: 'rejected' },
            ],
            gridSize: 6,
        },
        {
            name: 'from_amount',
            label: 'از مبلغ',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-coins" />,
            inputProps: digitsOnlyInput(),
            formatThousands: true,
            secondIcon: <span style={{ marginRight: '-5px' }}>ریال</span>,
            gridSize: 6,
        },
        {
            name: 'to_amount',
            label: 'تا مبلغ',
            component: 'Text',
            ltr: 'true',
            icon: <i className="fa-light fa-coins" />,
            inputProps: digitsOnlyInput(),
            formatThousands: true,
            secondIcon: <span style={{ marginRight: '-5px' }}>ریال</span>,
            gridSize: 6,
        },
        {
            name: 'from_date',
            label: 'از تاریخ',
            component: 'Date',
            icon: <i className="fa-light fa-calendar-days" />,
            startOnDay: true,
            readOnlyInput: true,
            gridSize: 6,
        },
        {
            name: 'to_date',
            label: 'تا تاریخ',
            component: 'Date',
            icon: <i className="fa-light fa-calendar-days" />,
            startOnDay: true,
            readOnlyInput: true,
            gridSize: 6,
        },
    ],
    validation: Yup.object().shape({
        id: Yup.string()
            .nullable()
            .matches(/^\d*$/, 'شناسه تسویه حساب باید عدد باشد')
            .test(
                'positive-id',
                'شناسه تسویه حساب باید بزرگتر از صفر باشد',
                (value) => !value || Number(value) > 0,
            ),
        iban: Yup.string()
            .nullable()
            .max(26, 'شماره شبا می‌تواند حداکثر 26 کاراکتر باشد')
            .matches(/^$|^IR\d{0,24}$/, 'شماره شبا معتبر نیست'),
        from_amount: Yup.string()
            .nullable()
            .matches(/^\d*$/, 'از مبلغ باید عدد باشد'),
        to_amount: Yup.string()
            .nullable()
            .matches(/^\d*$/, 'تا مبلغ باید عدد باشد')
            .test(
                'gte-from-amount',
                'تا مبلغ باید بزرگتر یا مساوی از مبلغ باشد',
                function (value) {
                    const fromAmount = this.parent?.from_amount;
                    if (!value || !fromAmount) return true;
                    return Number(value) >= Number(fromAmount);
                },
            ),
    }),
};
